import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useFarmServiceApi from 'hooks/useFarmServiceApi';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import { Context } from 'components/Store';
import { isNil } from 'lodash';
import {
  initialRequestState,
  onRequestStart,
  onRequestEnd,
  onRequestFail,
  onRequestSuccess
} from 'utilities/helpers/requestState';
import {
  FARM_CREATE_V2,
  FARM_CREATE_V2_BY_ORG,
  FARM_UPDATE_V2,
  FARM_UPDATE_V2_BY_ORG,
  FARM_DELETE_V2,
  FARM_DELETE_V2_BY_ORG,
  FARM_FETCH_V2,
  FARM_FETCH_V2_BY_ORG,
  FARMS_LIST_V2,
  FARMS_LIST_V2_BY_ORG,
  FARM_ARCHIVE_V2,
  FARM_ARCHIVE_V2_BY_ORG,
  FARM_UNARCHIVE_V2,
  FARM_UNARCHIVE_V2_BY_ORG
} from 'constants/farmServiceV2Flags';
import { NATIONAL_ZOOM } from '../helpers/constants';

const useFarmData = (id, executeFetch = true) => {
  const [state, setState] = useState();
  const [farms, setFarms] = useState([]);
  const [farm, setFarm] = useState();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(NATIONAL_ZOOM);
  const [archive, setArchive] = useState(initialRequestState);
  const savingRef = useRef(false);
  const [{ organization }, dispatch] = useContext(Context);
  const flags = useFlags();

  const fetchApi = useFarmServiceApi(
    'farm',
    FARM_FETCH_V2,
    FARM_FETCH_V2_BY_ORG
  );
  const listApi = useFarmServiceApi(
    'farms',
    FARMS_LIST_V2,
    FARMS_LIST_V2_BY_ORG
  );
  const createApi = useFarmServiceApi(
    'farm',
    FARM_CREATE_V2,
    FARM_CREATE_V2_BY_ORG
  );
  const updateApi = useFarmServiceApi(
    'farm',
    FARM_UPDATE_V2,
    FARM_UPDATE_V2_BY_ORG
  );
  const deleteApi = useFarmServiceApi(
    'farm',
    FARM_DELETE_V2,
    FARM_DELETE_V2_BY_ORG
  );
  const archiveApi = useFarmServiceApi(
    'farm',
    FARM_ARCHIVE_V2,
    FARM_ARCHIVE_V2_BY_ORG
  );
  const unarchiveApi = useFarmServiceApi(
    'farm',
    FARM_UNARCHIVE_V2,
    FARM_UNARCHIVE_V2_BY_ORG
  );

  const archiveResourcePath = featureFlagRenderItem(
    flags[FARM_ARCHIVE_V2],
    flags[FARM_ARCHIVE_V2_BY_ORG],
    organization?.id
  )
    ? 'farms'
    : 'farm';
  const unarchiveResourcePath = featureFlagRenderItem(
    flags[FARM_UNARCHIVE_V2],
    flags[FARM_UNARCHIVE_V2_BY_ORG],
    organization?.id
  )
    ? 'farms'
    : 'farm';

  const fetchFarms = useCallback(() => {
    const { promise, cancel } = listApi.fetch(
      null,
      null,
      { headers: { 'Local-Cache': true } },
      null,
      false
    );

    promise
      .then(({ data }) => setFarms(data))
      .catch(catchCancel)
      .catch(parseServerError(dispatch))
      .finally(() => setLoading(false));

    return {
      promise,
      cancel
    };
  }, [dispatch, listApi]);

  const fetchFarmById = useCallback(() => {
    setLoading(true);
    const { promise, cancel } = fetchApi.fetch(id);
    promise
      .then(({ data }) => setFarm(data))
      .catch(catchCancel)
      .catch(parseServerError(dispatch))
      .finally(() => setLoading(false));

    return {
      promise,
      cancel
    };
  }, [dispatch, id, fetchApi]);

  // loads farms or farm depending on id
  useEffect(() => {
    if (executeFetch) {
      if (!isNil(id)) fetchFarmById(id);
      else fetchFarms();
    }
  }, [fetchFarmById, fetchFarms, id, executeFetch]);

  const createFarm = useCallback(
    farmData => {
      if (savingRef.current) {
        return Promise.reject(new Error('save already in progress'));
      }
      savingRef.current = true;
      const { promise } = createApi.create(farmData);

      return promise
        .then(({ data }) => {
          setFarms(previousFarms => [...previousFarms, data]);
          return data;
        })
        .catch(catchCancel)
        .catch(parseServerError(dispatch))
        .finally(() => {
          savingRef.current = false;
        });
    },
    [dispatch, createApi]
  );

  const editFarm = useCallback(
    farmData => {
      setLoading(true);
      setSaving(true);

      if (savingRef.current) {
        setLoading(false);
        setSaving(false);
        return Promise.reject(new Error('edit already in progress'));
      }
      savingRef.current = true;
      const { promise } = updateApi.update(id, farmData);

      return promise
        .then(({ data }) => {
          setFarms(previousFarms =>
            previousFarms.map(currentFarm => {
              if (currentFarm.id === id)
                return {
                  ...currentFarm,
                  ...data
                };
              return currentFarm;
            })
          );
          setState('success');
          setFarm(data);
          return data;
        })
        .catch(catchCancel)
        .catch(parseServerError(dispatch))
        .catch(setState('error'))
        .finally(() => {
          savingRef.current = false;
          setLoading(false);
          setSaving(false);
        });
    },
    [dispatch, id, updateApi]
  );

  const archiveFarm = useCallback(
    farmId => {
      setArchive(curr => onRequestStart(curr));
      const { promise } = archiveApi
        .createChildApi({
          action: `${archiveResourcePath}/${farmId}/archive`
        })
        .post();

      promise
        .then(() => {
          setArchive(curr => onRequestSuccess(curr));
        })
        .catch(catchCancel)
        .catch(error => {
          setArchive(curr => onRequestFail(curr, error));
          parseServerError(dispatch)(error);
        })
        .finally(() => {
          setArchive(curr => onRequestEnd(curr));
        });
    },
    [dispatch, archiveApi, archiveResourcePath]
  );

  const unarchiveFarm = async farmId => {
    setArchive(curr => onRequestStart(curr));
    const unarchive = unarchiveApi.createChildApi({
      action: `${unarchiveResourcePath}/${farmId}/unarchive`
    });
    const { promise } = unarchive.post();
    promise
      .then(() => {
        setArchive(curr => onRequestSuccess(curr));
      })
      .catch(catchCancel)
      .catch(error => {
        setArchive(curr => onRequestFail(curr, error));
        parseServerError(dispatch)(error);
      })
      .finally(() => {
        setArchive(curr => onRequestEnd(curr));
      });
  };

  const deleteFarm = useCallback(() => {
    if (savingRef.current) {
      return Promise.reject(new Error('delete already in progress'));
    }
    savingRef.current = true;
    const { promise } = deleteApi.delete(id);

    return promise
      .then(({ data }) => {
        setFarms(previousFarms =>
          previousFarms.filter(currentFarm => currentFarm.id !== id)
        );
        return data;
      })
      .catch(catchCancel)
      .catch(parseServerError(dispatch))
      .finally(() => {
        savingRef.current = false;
        return savingRef.current;
      });
  }, [dispatch, id, deleteApi]);

  return {
    state,
    farms,
    farm,
    loading,
    saving,
    createFarm,
    fetchFarms,
    zoom,
    setZoom,
    fetchFarmById,
    editFarm,
    archive,
    archiveFarm,
    unarchiveFarm,
    deleteFarm
  };
};

export default useFarmData;
